/**
 * THE PRE-FLIGHT — what a commissioner checks before he runs the draw, and the console he runs it
 * from. `design_handoff_order_and_lottery` §2.
 *
 * The screen this replaces had one warning block and it was the wrong one. "Nine GMs have not
 * submitted a ranking" rendered in alarm red on the launcher, and rankings **close five days after
 * the draw** — slots are resolved at 5pm on 31 August, the draw is on the 26th, and the two have
 * nothing to do with each other. Nobody was late. A commissioner reading a red panel two minutes
 * before opening a live draw either delays it for no reason or learns to ignore red panels, and
 * both of those are worse than saying nothing.
 *
 * So readiness is FOUR LINES WITH THREE STATES, and the states are the point:
 *
 *   `ready`  — checked, and it holds.
 *   `check`  — genuinely blocking. Only the things the draw cannot be run correctly without.
 *   `later`  — true, relevant, and not tonight's problem. Neutral, grey, last.
 *
 * Every line is derived. A launcher whose reassurances are hard-coded is a launcher that says
 * "confirmed" on the one night somebody forgot to confirm it.
 */

import { LEAGUE_SIZE } from './proposals.js';
import { words } from './commishConsole.js';

export type Readiness = 'ready' | 'check' | 'later';

export interface ReadyLine {
  id: 'attendance' | 'finish' | 'toilet' | 'rankings';
  state: Readiness;
  /** The heading, in the words the screen shows. */
  text: string;
  /** One line under it, or empty. */
  note: string;
}

export interface LaunchInput {
  /** GMs in the room or on the call. */
  present: number;
  teams?: number;
  /** Has last season's finishing order been checked against the source? */
  finishConfirmed: boolean;
  finishSource: string;
  /** The Toilet Bowl winner, and whether anybody has actually confirmed him. */
  toiletBowl: string | null;
  toiletBowlConfirmed: boolean;
  /** Short name for display. */
  shortName: (gm: string) => string;
  /** How many GMs have ranked. Not a gate — see the note above. */
  ranked: number;
  /** When rankings close, for the line that says so. */
  ranksCloseOn: string;
  /** True once the draw has been recorded. */
  drawn: boolean;
}

export function readiness(input: LaunchInput): ReadyLine[] {
  const teams = input.teams ?? LEAGUE_SIZE;
  const quorumish = input.present >= teams - 1;

  return [
    {
      id: 'attendance',
      /*
       * NOT A BLOCKER, and deliberately. 2014-II-1 §5 asks for a "live setting where other GMs
       * will be present"; it does not set a number, and a draw held with eight in the room is
       * still a draw held in public. It is stated so a commissioner can decide, not gated so the
       * app can.
       */
      state: quorumish ? 'ready' : 'check',
      text: `${cap(words(input.present))} of ${words(teams)} present, in the room or on the call`,
      note: quorumish ? '' : 'The rules ask for a live setting rather than a quorum, so this is '
        + 'yours to judge.',
    },
    {
      id: 'finish',
      state: input.finishConfirmed ? 'ready' : 'check',
      text: input.finishConfirmed
        ? 'Last season’s finish confirmed against Yahoo'
        : 'Last season’s finish is not confirmed',
      note: input.finishSource,
    },
    {
      id: 'toilet',
      state: input.toiletBowlConfirmed && input.toiletBowl ? 'ready' : 'check',
      text: input.toiletBowl
        ? `${input.shortName(input.toiletBowl)} ${input.toiletBowlConfirmed ? 'confirmed' : 'not yet confirmed'} `
          + 'as the Toilet Bowl winner, so the second choosing position is his'
        : 'No Toilet Bowl winner recorded',
      note: input.toiletBowlConfirmed ? '' : 'Confirm him first — 2020-II-1 §3 places him before '
        + 'anybody chooses.',
    },
    {
      id: 'rankings',
      /*
       * ALWAYS `later`, whatever the count. This is the line that used to be a red alarm. It is
       * true — most of the league has not ranked — and it is not a reason to delay anything,
       * because ranking is a decision a GM makes AFTER he knows his turn.
       */
      state: 'later',
      text: 'Slot rankings are not needed tonight',
      note: `${input.ranked ? `${cap(words(input.ranked))} in so far. ` : ''}`
        + `They close on ${input.ranksCloseOn}, and a GM ranks better once he knows his turn.`,
    },
  ];
}

/** What still blocks the draw, in the words the console shows. Empty when nothing does. */
export function blocking(lines: readonly ReadyLine[]): string {
  const bad = lines.filter((l) => l.state === 'check');
  if (bad.length === 0) return '';
  if (bad.length === 1) return bad[0]!.text;
  return `${cap(words(bad.length))} things to settle first`;
}

/* -- the console -------------------------------------------------------------------------------- */

export interface LaunchConsole {
  line: string;
  primary: string;
  question: string;
  consequence: string;
  blocked: string;
}

export function launchConsole(
  input: LaunchInput,
  lines: readonly ReadyLine[],
  rehearsing: boolean,
): LaunchConsole {
  if (input.drawn) {
    return {
      line: 'The lottery has been run. The order is recorded and cannot be drawn again.',
      primary: '',
      question: '',
      consequence: '',
      blocked: 'Already drawn.',
    };
  }
  if (rehearsing) {
    return {
      line: 'Rehearsing. Nothing is saved and no GM has been notified.',
      primary: 'End the rehearsal',
      question: '',
      consequence: '',
      blocked: '',
    };
  }
  return {
    line: `${cap(words(input.present))} present. The draw decides the order GMs choose their slot, `
      + 'not the slots themselves.',
    primary: 'Run the lottery',
    question: 'Run the lottery for real?',
    /*
     * WHAT IT ACTUALLY DOES, and the second half is the half people forget: the draw settles the
     * ORDER OF CHOOSING (2020-II-1 §2), and nobody has a slot until the rankings resolve five days
     * later. A consequence line that said "the draft order is decided" would be wrong.
     */
    consequence: `${cap(words(input.teams ?? LEAGUE_SIZE))} screens switch to the reveal the moment `
      + 'you confirm, and the order is recorded with its seed. Nobody has a draft slot yet — those '
      + `resolve when rankings close on ${input.ranksCloseOn}.`,
    blocked: blocking(lines),
  };
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
